/**
 * pickerCache (perf) — wraps a raw `(search, fetchOne)` pair into a cached {@link PickerSource}.
 *
 * A section like Bundle deals or collection-list renders MANY pickers at once (one per block). Without a
 * cache each picker fired its OWN `GET` on every open, and each already-bound block fetched a FULL entity
 * just to label its trigger — which made the picker feel slow. This helper makes every picker backed by
 * the same source:
 *   • SHARE one search request (in-flight-deduped + cached by term), so N pickers hit the network once,
 *   • reopen instantly (served from cache),
 *   • resolve a trigger label from the already-loaded list instead of a per-entity GET.
 *
 * Each call to {@link createCachedPickerSource} owns its OWN cache instance, so products and collections
 * never collide. The cache is session-scoped with a short TTL so an entity created/renamed in the
 * dashboard (a separate app) still appears on the next open, and a full builder reload starts cold.
 */
import type { PickerItem, PickerSource } from '../inspector/controls/EntityPickerListbox';

const SEARCH_TTL_MS = 60_000;

interface SearchCacheEntry {
  at: number;
  promise: Promise<PickerItem[]>;
}

export interface PickerFetchers {
  /** Raw server search for a term (no caching) — returns a page of candidates. */
  search: (term: string) => Promise<PickerItem[]>;
  /** Raw single-entity fetch — the fallback used only when the id is not in a loaded list page. */
  fetchOne: (id: string) => Promise<PickerItem | null>;
  /**
   * Optional SYNCHRONOUS fallback for {@link PickerSource.peek} — a cross-reload store (e.g. the builder's
   * picked-product snapshot). Consulted only when this session's list cache has never seen the id, which is
   * exactly the cold-load case where the trigger used to print "…".
   */
  peek?: (id: string) => PickerItem | null;
}

export function createCachedPickerSource(fetchers: PickerFetchers): PickerSource {
  const searchCache = new Map<string, SearchCacheEntry>();
  // id → picker item, populated by every search page; lets a trigger label render without a full GET.
  const itemById = new Map<string, PickerItem>();

  const remember = (items: PickerItem[]): PickerItem[] => {
    for (const it of items) itemById.set(it.id, it);
    return items;
  };

  const search = (term: string): Promise<PickerItem[]> => {
    const cached = searchCache.get(term);
    if (cached && Date.now() - cached.at < SEARCH_TTL_MS) return cached.promise;

    const promise = fetchers.search(term).then(remember);
    // Cache the in-flight promise so concurrent pickers dedupe to ONE request; evict on failure so a
    // transient error isn't stuck in the cache and the next open retries.
    searchCache.set(term, { at: Date.now(), promise });
    promise.catch(() => {
      if (searchCache.get(term)?.promise === promise) searchCache.delete(term);
    });
    return promise;
  };

  const resolveOne = async (id: string): Promise<PickerItem | null> => {
    const known = itemById.get(id);
    if (known) return known;
    // Warm the shared (cached) list first — cheaper than a per-entity GET and shared across every picker
    // on the page, so N already-bound blocks resolve from ONE request. Fall back to `fetchOne` only when
    // the selection is beyond the first search page (or the list fetch failed).
    try {
      await search('');
      const fromList = itemById.get(id);
      if (fromList) return fromList;
    } catch {
      // list warm failed — fall through to the single-entity fetch below.
    }
    const one = await fetchers.fetchOne(id);
    if (one) itemById.set(id, one);
    return one;
  };

  // Synchronous: this session's list cache first, then whatever the caller persists across reloads.
  const peek = (id: string): PickerItem | null => itemById.get(id) ?? fetchers.peek?.(id) ?? null;

  return { search, resolveOne, peek };
}
